import { ClusterDetails_cluster } from "@saleor/cluster/types/ClusterDetails";
import { ClusterUpdate_clusterUpdate_errors } from "@saleor/cluster/types/ClusterUpdate";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { maybe } from "@saleor/misc";
import { useCreateClusterValidation } from "@saleor/utils/validators/cluster";
import { hasNoError } from "@saleor/utils/validators/sales";
import React from "react";

import ClusterContent from "../ClusterContent/ClusterContent";
import ClusterInfo from "../ClusterInfo";

export interface ClusterFormData {
  lowerPrepTimeDays: string;
  lowerPrepTimeHours: string;
  lowerPrepTimeMins: string;
  name: string;
  productType: string;
  upperPrepTimeDays: string;
  upperPrepTimeHours: string;
  upperPrepTimeMins: string;
}

export interface ClusterDetailsPageProps {
  errors: ClusterUpdate_clusterUpdate_errors[];
  data: ClusterDetails_cluster;
  onDelete: () => void;
  onBack: () => void;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit(data: ClusterFormData);
}

const ClusterDetailsPage: React.FC<ClusterDetailsPageProps> = props => {
  const {
    onDelete,
    onBack,
    disabled,
    saveButtonBarState,
    onSubmit,
    data,
    errors
  } = props;

  const [validator, setValidator] = React.useState({
    lowerPrepTimeDays: null,
    lowerPrepTimeHours: null,
    lowerPrepTimeMins: null,
    name: null,
    productType: null,
    upperPrepTimeDays: null,
    upperPrepTimeHours: null,
    upperPrepTimeMins: null
  });

  const initialForm: ClusterFormData = {
    lowerPrepTimeDays: maybe(() => data.lowerPrepTimeDays.toString(), "0"),
    lowerPrepTimeHours: maybe(() => data.lowerPrepTimeHours.toString(), "0"),
    lowerPrepTimeMins: maybe(() => data.lowerPrepTimeMins.toString(), "0"),
    name: maybe(() => data.name, ""),
    productType: maybe(() => data.productType.id, ""),
    upperPrepTimeDays: maybe(() => data.upperPrepTimeDays.toString(), "0"),
    upperPrepTimeHours: maybe(() => data.upperPrepTimeHours.toString(), "0"),
    upperPrepTimeMins: maybe(() => data.upperPrepTimeMins.toString(), "0")
  };

  const handleSubmit = event => {
    const validate = useCreateClusterValidation(event);
    setValidator(validate);
    if (hasNoError(validate)) {
      onSubmit(event);
    }
  };

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ data, change, hasChanged, submit }) => {
        const breadcrumbDirectory = [
          {
            name: "Clusters",
            url: "/clusters"
          },
          {
            name: data && data.name
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={data && data.name} />
            </div>
            <div>
              <ClusterInfo
                errors={errors}
                data={data}
                onChange={change}
                disabled={false}
                localError={validator}
              />
              <CardSpacer />
              <ClusterContent
                data={data}
                errors={errors}
                onChange={change}
                disabled={false}
                localError={validator}
              />
            </div>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onDelete={onDelete}
              onSave={submit}
              state={saveButtonBarState}
            />
          </Container>
        );
      }}
    </Form>
  );
};

export default ClusterDetailsPage;
